import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import {
  isYouTubePrivacyStatus,
  YOUTUBE_MADE_FOR_KIDS_URL,
  YOUTUBE_PRIVACY_STATUSES,
  YouTubePrivacyStatus,
  YouTubeSettingsDraft,
} from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../../common/types';
import { youtubeMessages } from './youtubeMessages';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface YouTubeSettingsFieldsProps {
  draft: YouTubeSettingsDraft;
  /** The start of the element ids: one page can show the fields two times. */
  idPrefix?: string;
  onPrivacy: (status: YouTubePrivacyStatus) => void;
  onMadeForKids: (value: boolean) => void;
  /** False: only the privacy status shows (the posting default of the channel keeps nothing else). */
  showMadeForKids?: boolean;
}

const LEVEL_MESSAGES = {
  public: youtubeMessages.privacyPublic,
  unlisted: youtubeMessages.privacyUnlisted,
  private: youtubeMessages.privacyPrivate,
} as const;

/**
 * The two YouTube answers: who can see the video, and "Made for kids". The Composer section and
 * the launch questions show both; the posting default of the channel shows only the first. "Made
 * for kids" has no selected value until the user answers, for each post and each campaign: YouTube
 * wants the uploader to declare it, and it is never stored for the channel.
 */
const YouTubeSettingsFieldsComponent: React.FC<YouTubeSettingsFieldsProps> = ({
  draft,
  idPrefix = 'youtube',
  onPrivacy,
  onMadeForKids,
  showMadeForKids = true,
}) => {
  const intl = useIntl();
  const changePrivacy = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isYouTubePrivacyStatus(event.target.value)) {
        onPrivacy(event.target.value);
      }
    },
    [onPrivacy]
  );
  const answerYes = useCallback(() => onMadeForKids(true), [onMadeForKids]);
  const answerNo = useCallback(() => onMadeForKids(false), [onMadeForKids]);

  return (
    <>
      <div className={styles.field}>
        <label className={styles.eyebrow} htmlFor={`${idPrefix}-privacy`}>
          {intl.formatMessage(youtubeMessages.privacyLabel)}
        </label>
        <Input
          id={`${idPrefix}-privacy`}
          type="select"
          value={draft.privacyStatus ?? ''}
          invalid={draft.privacyStatus === null}
          onChange={changePrivacy}
        >
          <option value="" disabled>
            {intl.formatMessage(youtubeMessages.privacyPlaceholder)}
          </option>
          {YOUTUBE_PRIVACY_STATUSES.map((status) => (
            <option key={status} value={status}>
              {intl.formatMessage(LEVEL_MESSAGES[status])}
            </option>
          ))}
        </Input>
      </div>
      {showMadeForKids ? (
        <div className={styles.field} role="radiogroup" aria-label={intl.formatMessage(youtubeMessages.kidsLabel)}>
          <span className={styles.eyebrow}>{intl.formatMessage(youtubeMessages.kidsLabel)}</span>
          <div className={styles.group}>
            <div className={styles.row}>
              <Input
                id={`${idPrefix}-kids-yes`}
                type="radio"
                name={`${idPrefix}-kids`}
                checked={draft.madeForKids === true}
                onChange={answerYes}
              />
              <label className={styles.rowText} htmlFor={`${idPrefix}-kids-yes`}>
                <span className={styles.rowTitle}>{intl.formatMessage(youtubeMessages.kidsYes)}</span>
              </label>
            </div>
            <div className={styles.row}>
              <Input
                id={`${idPrefix}-kids-no`}
                type="radio"
                name={`${idPrefix}-kids`}
                checked={draft.madeForKids === false}
                onChange={answerNo}
              />
              <label className={styles.rowText} htmlFor={`${idPrefix}-kids-no`}>
                <span className={styles.rowTitle}>{intl.formatMessage(youtubeMessages.kidsNo)}</span>
              </label>
            </div>
          </div>
          <p className={styles.small}>
            {withBrandWord(intl.formatMessage(youtubeMessages.kidsNote))}{' '}
            <a href={YOUTUBE_MADE_FOR_KIDS_URL} target="_blank" rel="noreferrer">
              {intl.formatMessage(youtubeMessages.kidsLearnMore)}
            </a>
          </p>
        </div>
      ) : null}
    </>
  );
};

export const YouTubeSettingsFields = TypedMemo(YouTubeSettingsFieldsComponent);
